// public/v3/src/components/order-doc/destination-editor.jsx
//
// "Editable Send-to destination" (Phase 1, Task 3) — the inline control that
// lets a provider set/correct where a recipient-routed order is sent: a referral
// (the generic OrderCard branch) or a facility-routed lab/imaging requisition
// (LabOrderCard). It renders ONLY when the card already routes to a recipient —
// the caller gates on `card.sendToFacility || card.recipientContact` (lab) or
// `order.type === 'referral'` (order-chat). Quest, medications, and plain e-Req
// labs with no recipient never render it.
//
// Shape: a "Send to:" label, the current destination (or "— not set"), a small
// Fax|Email segmented toggle, and a click-to-edit inline input (modeled on the
// lab card's `TestNameEditor`): the value shows as a button; clicking turns it
// into an input that commits on Enter/blur. Committing calls
//   onSet(channel, value)   // channel: 'fax' | 'email'
// which the caller threads to the server `set-destination` edit. The channel
// state defaults from props (fax if `value.fax`, else email if `value.email`,
// else 'fax'); switching the toggle re-seeds the field from that channel's
// current value so the displayed text always matches the active channel.
//
// Validation lives server-side (Task 1); this component surfaces a failure
// inline — via the `error` prop (the consumer can pass the rejected-edit
// message) or a local error set when an `onSet` that returns a promise rejects.

import React, { useState } from 'react';

const TOGGLE = {
  on:  { bg: '#0f766e', fg: '#ffffff', border: '#0f766e' },
  off: { bg: '#ffffff', fg: '#374151', border: '#d1d5db' },
};

// Per-channel placeholder + format hint so the provider knows what shape the
// field expects (a fax is digits; an email is an address).
const CHANNEL_META = {
  fax:   { label: 'Fax',   placeholder: '(555) 123-4567', hint: 'digits — the recipient fax number' },
  email: { label: 'Email', placeholder: 'name@practice.org', hint: 'a recipient email address' },
};

function defaultChannel(value) {
  if (value && value.fax) return 'fax';
  if (value && value.email) return 'email';
  return 'fax';
}

function ToggleButton({ active, onClick, children }) {
  const c = active ? TOGGLE.on : TOGGLE.off;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={active}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{
        padding: '3px 10px', fontSize: 12, fontWeight: 600,
        color: c.fg, background: c.bg, border: `1px solid ${c.border}`,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

export function DestinationEditor({ value, onSet, error: errorProp }) {
  const v = value || {};
  const [channel, setChannel] = useState(() => defaultChannel(v));
  const currentForChannel = channel === 'email' ? (v.email || '') : (v.fax || '');
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(currentForChannel);
  const [localError, setLocalError] = useState(null);
  const error = errorProp || localError;

  const meta = CHANNEL_META[channel] || CHANNEL_META.fax;

  // Switch the active channel and re-seed the field from THAT channel's current
  // value, so the shown text never lags behind the toggle.
  function pickChannel(next) {
    if (next === channel) return;
    setChannel(next);
    setText(next === 'email' ? (v.email || '') : (v.fax || ''));
    setEditing(false);
    setLocalError(null);
  }

  function startEdit() {
    setText(currentForChannel);
    setLocalError(null);
    setEditing(true);
  }

  function commit() {
    setEditing(false);
    const next = text.trim();
    if (!next || next === currentForChannel) {
      setText(currentForChannel); // unchanged / blank → revert, no round-trip
      return;
    }
    if (!onSet) return;
    let r;
    try {
      r = onSet(channel, next);
    } catch (e) {
      setLocalError((e && e.message) || 'Could not set the destination.');
      return;
    }
    // If onSet returns a promise, surface a rejection inline.
    if (r && typeof r.then === 'function') {
      r.then(
        () => setLocalError(null),
        (e) => setLocalError((e && e.message) || 'Could not set the destination.'),
      );
    }
  }

  return (
    <div style={{ marginTop: 8, fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, color: '#374151' }}>Send to:</span>

        {/* Fax|Email segmented toggle */}
        <span style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden' }}>
          <span style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #d1d5db' }}>
            <ToggleButton active={channel === 'fax'} onClick={() => pickChannel('fax')}>Fax</ToggleButton>
            <ToggleButton active={channel === 'email'} onClick={() => pickChannel('email')}>Email</ToggleButton>
          </span>
        </span>

        {/* Click-to-edit current value (modeled on TestNameEditor). */}
        {editing ? (
          <input
            autoFocus
            type={channel === 'email' ? 'email' : 'text'}
            value={text}
            placeholder={meta.placeholder}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              else if (e.key === 'Escape') { e.preventDefault(); setText(currentForChannel); setEditing(false); }
            }}
            aria-label={`${meta.label} destination`}
            style={{
              flex: '1 1 160px', minWidth: 120, fontSize: 13, color: '#111',
              border: '1px solid #99f6e4', borderRadius: 6, padding: '3px 6px',
            }}
          />
        ) : (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); startEdit(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); startEdit(); }
            }}
            title={`Set the ${meta.label.toLowerCase()} destination`}
            style={{
              color: currentForChannel ? '#111' : '#9ca3af',
              fontWeight: currentForChannel ? 600 : 400,
              cursor: 'text', borderBottom: '1px dashed #cbd5e1', paddingBottom: 1,
            }}
          >
            {currentForChannel || '— not set'}
          </span>
        )}
      </div>

      <div style={{ marginTop: 3, fontSize: 11, color: '#9ca3af' }}>{meta.hint}</div>

      {error && (
        <div role="alert" style={{ marginTop: 3, fontSize: 12, color: '#b91c1c' }}>{error}</div>
      )}
    </div>
  );
}

export default DestinationEditor;
